import { useAuth, useClerk } from '@clerk/clerk-react'
import { captureMessage } from '@sentry/react'
import { QueryClient } from '@tanstack/react-query'
import Axios, { type InternalAxiosRequestConfig, isAxiosError } from 'axios'
import dayjs from 'dayjs'
import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router'

import { getNinjaAPI } from 'app/api/api'
import { isPartnerNotEnabledError } from 'app/utils/partnerNotEnabledError'
import type {
    AuthorizeApplicationRequestApiType,
    BankAccountConnectionRequestType,
    ChangePaymentMethodGetPadAgreementRequestSchema,
    ChangePaymentMethodLinkAccountRequestSchema,
    EmploymentInfoRequestType,
    FlinksPaymentCheckRequestSchema,
    FlinksSetAccountRequestSchema,
    FlinksTrackEventRequestSchema,
    LanguagePreferenceRequestApiType,
    LoanPurposeRequestType,
    OfferTermRequestType,
    OnfidoTokenRequestApiType,
    PartnerConfigRequestSchema,
    PaymentBreakdownRequestSchema,
    PaymentDeferralCompleteRequestSchema,
    PaymentDeferralProcessSelectedDateRequestSchema,
    PaymentProtectionSelectionRequestApiType,
    PaymentScheduleChangeCompleteRequestSchema,
    PaymentScheduleChangeProcessSelectedDateRequestSchema,
    PaymentScheduleRequestType,
    PaymentScheduleValidationRequestSchema,
    PhoneNumberRequestType,
    ProfileSettingsUpdateRequestSchema,
    QueryParamsRequestType,
    QuerySessionRequestApiType,
    SessionTrackingInfoRequestType,
    UpdatePiiDataRequestApiType,
    V3ApplicationRepaymentScheduleRequestType
} from 'app/api/api.schemas'
import { useAuthTokenOverride } from 'app/contexts/AuthTokenContext'
import { useEmploymentInfo, useFinancialInfo, useHousingInfo, useIdInfo, useLoanInfo, usePersonalInfo } from 'app/data/prequalStore'
import { API_URL, HOUSE_TYPES, OFFER_ROUTES, PERSONAL_LOAN_ROUTES, VEHICLE_OWNERSHIP_VALUES } from 'app/utils/constants'
import { getPostHogDistinctIdHeaders } from 'app/utils/posthogHeaders'

export const BASE_API_URL = API_URL
Axios.defaults.baseURL = BASE_API_URL

Axios.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    try {
        config.headers['X-Request-ID'] = globalThis.crypto?.randomUUID?.() ?? fallbackRequestId()
    } catch {
        config.headers['X-Request-ID'] = fallbackRequestId()
    }

    const posthogHeaders = getPostHogDistinctIdHeaders()
    for (const [header, value] of Object.entries(posthogHeaders)) {
        config.headers[header] = value
    }

    return config
})

function fallbackRequestId(): string {
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
}

const defaultQueryConfig = {
    staleTime: Infinity,
    cacheTime: Infinity,
    retry: false,
    refetchOnMount: 'always' as const,
    refetchOnWindowFocus: false,
    useErrorBoundary: true
}
const defaultMutateConfig = {
    retry: false,
    useErrorBoundary: true
}

export const queryClient = new QueryClient({
    defaultOptions: {
        queries: defaultQueryConfig,
        mutations: defaultMutateConfig
    }
})

export const parseHeaderWithClerkToken = (token: string | null) => {
    return {
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
        }
    }
}

export const useApiPrequal = (partnerId?: string) => {
    const api = getNinjaAPI()
    const { firstName, lastName, email, dateOfBirth, phoneNumber, address1, address2, province, city, postalCode, residencyStatus, residencyStatusOther } =
        usePersonalInfo()
    const { annualIncome, levelOfEducation, employmentStatus, occupationType, jobTitle, jobStartDate, employerName } = useEmploymentInfo()
    const { monthlyHousingPayment, livedAtAddressSince, housingStatus } = useHousingInfo()
    const { loanInLastThreeMonths, savingsCheckingAccounts, savingsInvestmentAccounts, vehicleOwnership } = useFinancialInfo()
    const { loanAmount, loanPurpose, loanPurposeOther, languagePreference, queryParams } = useLoanInfo()
    const { partnerSessionId, campaignId } = useIdInfo()

    return {
        validatePhoneNumber: async (requestedPhoneNumber: string) => {
            try {
                const { data } = await api.personalLoansV2ApiPrequalificationPrequalificationValidationPhoneNumber({
                    phone_number: requestedPhoneNumber
                })
                return data
            } catch (error) {
                captureMessage('Error validating phone number', {
                    level: 'error',
                    extra: { error, requestedPhoneNumber }
                })
                throw error
            }
        },
        validateEmail: async (requestedEmail: string) => {
            try {
                const { data } = await api.personalLoansV2ApiPrequalificationPrequalificationValidationEmail({
                    email_address: requestedEmail
                })
                return data
            } catch (error) {
                captureMessage('Error validating email', {
                    level: 'error',
                    extra: { error, requestedEmail }
                })
                throw error
            }
        },
        prequalify: async () => {
            try {
                const payload = {
                    borrower: {
                        home_address: {
                            address_1: address1,
                            address_2: address2,
                            province_code: province,
                            city,
                            country_code: 'CA',
                            postal_code: postalCode
                        },
                        personal_info: {
                            legal_first_name: firstName,
                            legal_last_name: lastName,
                            mobile_number: phoneNumber,
                            email_address: email,
                            date_of_birth: dateOfBirth?.format ? dateOfBirth.format('YYYY-MM-DD') : ''
                        },
                        employment_info: {
                            employment_status: employmentStatus,
                            occupation_type: occupationType,
                            job_title: jobTitle,
                            employer_name: employerName,
                            start_date: jobStartDate ? dayjs(jobStartDate, 'MM/YYYY').format('YYYY-MM-DD') : '',
                            annual_income: annualIncome
                        },
                        level_of_education: levelOfEducation,
                        residency_status: residencyStatus,
                        financial_info: (() => {
                            if (
                                (loanInLastThreeMonths !== 'YES' && loanInLastThreeMonths !== 'NO') ||
                                !VEHICLE_OWNERSHIP_VALUES.includes(vehicleOwnership as (typeof VEHICLE_OWNERSHIP_VALUES)[number])
                            ) {
                                return undefined
                            }
                            return {
                                loan_in_last_three_months: loanInLastThreeMonths === 'YES',
                                amount_savings_chequing: savingsCheckingAccounts,
                                amount_investment: savingsInvestmentAccounts,
                                vehicle_ownership: vehicleOwnership
                            }
                        })(),
                        annual_gross_income: annualIncome,
                        purpose_of_loan: loanPurpose,
                        purpose_of_loan_other: loanPurposeOther,
                        residency_status_other: residencyStatusOther,
                        is_homeowner: housingStatus === HOUSE_TYPES.OWNED,
                        monthly_home_payment: monthlyHousingPayment,
                        years_at_address: livedAtAddressSince ? dayjs().diff(livedAtAddressSince, 'year') : 0,
                        requested_loan_amount: loanAmount
                    },
                    locale: languagePreference,
                    is_email_consent: true,
                    partner_session_id: partnerSessionId,
                    campaign_id: campaignId,
                    is_test_form_used: false,
                    query_params: queryParams,
                    partner_id: partnerId || ''
                }
                const { data } = await api.personalLoansV2ApiPrequalificationCreateAffiliatePrequalificationRequest(payload, {
                    headers: {
                        'Content-Type': 'application/json'
                    }
                })
                return data
            } catch (error) {
                if (isPartnerNotEnabledError(error)) {
                    throw error
                }
                captureMessage('Error during prequalification', {
                    level: 'error',
                    extra: {
                        error,
                        payload: {
                            firstName,
                            lastName,
                            email,
                            employmentStatus,
                            occupationType
                        }
                    }
                })
                throw error
            }
        }
    }
}
export const usePublicApi = () => {
    const api = getNinjaAPI()

    return {
        getPartnerConfiguration: async (payload: PartnerConfigRequestSchema) => {
            try {
                const { data } = await api.personalLoansV2ApiPartnerConfigurationPartnerConfiguration(payload, {
                    headers: {
                        'Content-Type': 'application/json'
                    }
                })
                return data
            } catch (error) {
                if (isPartnerNotEnabledError(error)) {
                    throw error
                }
                // Network errors (ERR_NETWORK) are client-side issues like lost connection,
                // ad blockers, or user navigation - log as warning since server is not at fault
                const isNetworkError = isAxiosError(error) && error.code === 'ERR_NETWORK'

                captureMessage('Error getting partner configuration', {
                    level: isNetworkError ? 'warning' : 'error',
                    extra: {
                        error,
                        payload,
                        partnerId: payload.partner_id,
                        isNetworkError
                    }
                })
                throw error
            }
        },
        getOfferData: async (applicationId: string) => {
            try {
                const { data } = await api.personalLoansV2ApiOfferGetOfferData(applicationId)
                return data
            } catch (error) {
                if (isPartnerNotEnabledError(error)) {
                    throw error
                }
                captureMessage('Error getting public offer data', {
                    level: 'error',
                    extra: {
                        error,
                        applicationId
                    }
                })
                throw error
            }
        },
        saveQueryParams: async (applicationId: string, payload: QueryParamsRequestType) => {
            try {
                const { data } = await api.originationApiSaveQueryParams(applicationId, payload)
                return data
            } catch (error) {
                captureMessage('Error saving query params', {
                    level: 'error',
                    extra: {
                        error,
                        applicationId
                    }
                })
                throw error
            }
        },

        saveSessionTrackingInfo: async (applicationId: string, payload: SessionTrackingInfoRequestType) => {
            try {
                const { data } = await api.originationApiSaveSessionTrackingInfo(applicationId, payload)
                return data
            } catch (error) {
                captureMessage('Error saving session tracking info', {
                    level: 'error',
                    extra: {
                        error,
                        applicationId
                    }
                })
                throw error
            }
        }
    }
}

export const useApiPersonalLoan = () => {
    const authTokenOverride = useAuthTokenOverride()
    const api = getNinjaAPI()
    const { getToken: getClerkToken, userId, isSignedIn, isLoaded } = useAuth()
    const { signOut } = useClerk()
    const navigate = useNavigate()
    const { search } = window.location

    // Use a ref to track isLoaded so we can access it in async functions
    const isLoadedRef = useRef(isLoaded)
    const isSignedInRef = useRef(isSignedIn)

    // Update the ref whenever isLoaded changes
    useEffect(() => {
        isLoadedRef.current = isLoaded
        isSignedInRef.current = isSignedIn
    }, [isLoaded, isSignedIn])

    /**
     * Wait for Clerk to be loaded before proceeding
     * @param timeout - Maximum time to wait in milliseconds (default: 10000ms)
     * @returns Promise that resolves when Clerk is loaded
     */
    async function waitForClerkToLoad(timeout = 10000): Promise<void> {
        if (isLoadedRef.current) {
            return
        }

        const startTime = Date.now()
        return new Promise((resolve, reject) => {
            const intervalId = setInterval(() => {
                if (isLoadedRef.current) {
                    clearInterval(intervalId)
                    resolve()
                } else if (Date.now() - startTime >= timeout) {
                    clearInterval(intervalId)
                    reject(new Error('Clerk failed to load within timeout period'))
                }
            }, 100) // Check every 100ms
        })
    }

    /**
     * Get a Clerk token for the current user, if no token is found, log an error and return null
     * If user is signed in but getToken returns null then try to force a new token with skipCache: True
     * https://clerk.com/docs/guides/sessions/force-token-refresh#get-token-skip-cache-true
     * If still null then return user to Offer page to log in again.
     * @param method - The method being called
     * @returns The Clerk token
     */
    async function getToken(method: string, applicationId: string, skipCache: boolean = false): Promise<string | null> {
        if (authTokenOverride) {
            return authTokenOverride.getToken()
        }

        // Wait for Clerk to finish loading
        try {
            await waitForClerkToLoad()
        } catch (error) {
            captureMessage('Clerk did not load in time', {
                level: 'error',
                extra: {
                    error,
                    method,
                    applicationId,
                    isLoaded: isLoadedRef.current
                }
            })
            try {
                await signOut()
            } catch (error) {
                captureMessage('Error Clerk signing out after Clerk did not load in time', {
                    level: 'error',
                    extra: {
                        error
                    }
                })
            }
            navigate(`/${PERSONAL_LOAN_ROUTES.ROOT}/${applicationId}/${OFFER_ROUTES.INDEX}${search}`)
            throw new Error('CLERK_LOADED_MISSING_TOKEN_ERROR', { cause: error })
        }

        let token = await getClerkToken({ skipCache })
        if (!token) {
            if (isSignedInRef.current) {
                token = await getClerkToken({ skipCache: true })
            }

            if (!token) {
                captureMessage('No Clerk token found', {
                    level: 'error',
                    extra: {
                        userId,
                        method,
                        isSignedIn: isSignedInRef.current,
                        isLoaded: isLoadedRef.current
                    }
                })
                try {
                    await signOut()
                } catch (error) {
                    captureMessage('Error Clerk signing out after No Clerk token found', {
                        level: 'error',
                        extra: {
                            error
                        }
                    })
                }
                navigate(`/${PERSONAL_LOAN_ROUTES.ROOT}/${applicationId}/${OFFER_ROUTES.INDEX}${search}`)
                throw new Error('CLERK_LOADED_MISSING_TOKEN_ERROR')
            }
        }
        return token
    }

    return {
        savePhoneNumber: async (applicationId: string, payload: PhoneNumberRequestType) => {
            try {
                const clerkToken = await getToken('savePhoneNumber', applicationId)
                const { data } = await api.originationApiSavePhoneNumber(applicationId, payload, parseHeaderWithClerkToken(clerkToken))
                return data
            } catch (error) {
                let errorToCapture = error
                if (isAxiosError(error) && error.response?.status === 401) {
                    try {
                        const freshClerkToken = await getToken('savePhoneNumber', applicationId, true)
                        const { data } = await api.originationApiSavePhoneNumber(applicationId, payload, parseHeaderWithClerkToken(freshClerkToken))
                        return data
                    } catch (innerError) {
                        errorToCapture = innerError
                    }
                }
                captureMessage('Error saving phone number', {
                    level: 'error',
                    extra: {
                        error: errorToCapture,
                        applicationId,
                        userId
                    }
                })
                throw errorToCapture
            }
        },
        saveOfferTerms: async (applicationId: string, payload: OfferTermRequestType) => {
            try {
                const clerkToken = await getToken('saveOfferTerms', applicationId)
                const { data } = await api.originationApiSaveOfferTerm(applicationId, payload, parseHeaderWithClerkToken(clerkToken))
                return data
            } catch (error) {
                let errorToCapture = error
                if (isAxiosError(error) && error.response?.status === 401) {
                    try {
                        const freshClerkToken = await getToken('saveOfferTerms', applicationId, true)
                        const { data } = await api.originationApiSaveOfferTerm(applicationId, payload, parseHeaderWithClerkToken(freshClerkToken))
                        return data
                    } catch (innerError) {
                        errorToCapture = innerError
                    }
                }
                captureMessage('Error saving offer terms', {
                    level: 'error',
                    extra: {
                        error: errorToCapture,
                        applicationId,
                        userId
                    }
                })
                throw errorToCapture
            }
        },
        saveLoanPurpose: async (applicationId: string, payload: LoanPurposeRequestType) => {
            try {
                const clerkToken = await getToken('saveLoanPurpose', applicationId)
                const { data } = await api.originationApiSaveLoanPurpose(applicationId, payload, parseHeaderWithClerkToken(clerkToken))
                return data
            } catch (error) {
                let errorToCapture = error
                if (isAxiosError(error) && error.response?.status === 401) {
                    try {
                        const freshClerkToken = await getToken('saveLoanPurpose', applicationId, true)
                        const { data } = await api.originationApiSaveLoanPurpose(applicationId, payload, parseHeaderWithClerkToken(freshClerkToken))
                        return data
                    } catch (innerError) {
                        errorToCapture = innerError
                    }
                }
                captureMessage('Error saving loan purpose', {
                    level: 'error',
                    extra: {
                        error: errorToCapture,
                        applicationId,
                        userId
                    }
                })
                throw errorToCapture
            }
        },
        savePaymentSchedule: async (applicationId: string, payload: PaymentScheduleRequestType) => {
            try {
                const clerkToken = await getToken('savePaymentSchedule', applicationId)
                const { data } = await api.originationApiSavePaymentSchedule(applicationId, payload, parseHeaderWithClerkToken(clerkToken))
                return data
            } catch (error) {
                let errorToCapture = error
                if (isAxiosError(error) && error.response?.status === 401) {
                    try {
                        const freshClerkToken = await getToken('savePaymentSchedule', applicationId, true)
                        const { data } = await api.originationApiSavePaymentSchedule(applicationId, payload, parseHeaderWithClerkToken(freshClerkToken))
                        return data
                    } catch (innerError) {
                        errorToCapture = innerError
                    }
                }
                captureMessage('Error saving payment schedule', {
                    level: 'error',
                    extra: {
                        error: errorToCapture,
                        applicationId,
                        userId
                    }
                })
                throw errorToCapture
            }
        },
        saveCompletedOnfidoWorkflow: async (applicationId: string) => {
            try {
                const clerkToken = await getToken('saveCompletedOnfidoWorkflow', applicationId)
                const { data } = await api.originationApiUserCompletedOnfidoWorkflow(applicationId, parseHeaderWithClerkToken(clerkToken))
                return data
            } catch (error) {
                let errorToCapture = error
                if (isAxiosError(error) && error.response?.status === 401) {
                    try {
                        const freshClerkToken = await getToken('saveCompletedOnfidoWorkflow', applicationId, true)
                        const { data } = await api.originationApiUserCompletedOnfidoWorkflow(applicationId, parseHeaderWithClerkToken(freshClerkToken))
                        return data
                    } catch (innerError) {
                        errorToCapture = innerError
                    }
                }
                captureMessage('Error saving completed Onfido workflow', {
                    level: 'error',
                    extra: {
                        error: errorToCapture,
                        applicationId
                    }
                })
                throw errorToCapture
            }
        },

        getPaymentProtectionData: async (applicationId: string) => {
            try {
                const clerkToken = await getToken('getPaymentProtectionData', applicationId)
                const { data } = await api.originationApiRetrievePaymentProtectionData(applicationId, parseHeaderWithClerkToken(clerkToken))
                return data
            } catch (error) {
                let errorToCapture = error
                if (isAxiosError(error) && error.response?.status === 401) {
                    try {
                        const freshClerkToken = await getToken('getPaymentProtectionData', applicationId, true)
                        const { data } = await api.originationApiRetrievePaymentProtectionData(applicationId, parseHeaderWithClerkToken(freshClerkToken))
                        return data
                    } catch (innerError) {
                        errorToCapture = innerError
                    }
                }
                captureMessage('Error getting payment protection data', {
                    level: 'error',
                    extra: {
                        error: errorToCapture,
                        applicationId,
                        userId
                    }
                })
                throw errorToCapture
            }
        },
        savePaymentProtectionSelection: async (applicationId: string, payload: PaymentProtectionSelectionRequestApiType) => {
            try {
                const clerkToken = await getToken('savePaymentProtectionSelection', applicationId)
                const { data } = await api.originationApiSavePaymentProtectionSelection(applicationId, payload, parseHeaderWithClerkToken(clerkToken))
                return data
            } catch (error) {
                let errorToCapture = error
                if (isAxiosError(error) && error.response?.status === 401) {
                    try {
                        const freshClerkToken = await getToken('savePaymentProtectionSelection', applicationId, true)
                        const { data } = await api.originationApiSavePaymentProtectionSelection(
                            applicationId,
                            payload,
                            parseHeaderWithClerkToken(freshClerkToken)
                        )
                        return data
                    } catch (innerError) {
                        errorToCapture = innerError
                    }
                }
                captureMessage('Error saving payment protection selection', {
                    level: 'error',
                    extra: {
                        error: errorToCapture,
                        applicationId
                    }
                })
                throw errorToCapture
            }
        },
        connectBankAccount: async (applicationId: string, payload: BankAccountConnectionRequestType) => {
            try {
                const clerkToken = await getToken('connectBankAccount', applicationId)
                const { data } = await api.originationApiBankAccountConnect(payload, parseHeaderWithClerkToken(clerkToken))
                return data
            } catch (error) {
                let errorToCapture = error
                if (isAxiosError(error) && error.response?.status === 401) {
                    try {
                        const freshClerkToken = await getToken('connectBankAccount', applicationId, true)
                        const { data } = await api.originationApiBankAccountConnect(payload, parseHeaderWithClerkToken(freshClerkToken))
                        return data
                    } catch (innerError) {
                        errorToCapture = innerError
                    }
                }
                captureMessage('Error connecting bank account', {
                    level: 'error',
                    extra: {
                        error: errorToCapture,
                        payload
                    }
                })
                throw errorToCapture
            }
        },
        getBankAccountStatus: async (applicationId: string, flinksAccountId: string) => {
            try {
                const clerkToken = await getToken('getBankAccountStatus', applicationId)
                const { data } = await api.originationApiBankAccountStatus(flinksAccountId, parseHeaderWithClerkToken(clerkToken))
                return data
            } catch (error) {
                let errorToCapture = error
                if (isAxiosError(error) && error.response?.status === 401) {
                    try {
                        const freshClerkToken = await getToken('getBankAccountStatus', applicationId, true)
                        const { data } = await api.originationApiBankAccountStatus(flinksAccountId, parseHeaderWithClerkToken(freshClerkToken))
                        return data
                    } catch (innerError) {
                        errorToCapture = innerError
                    }
                }
                captureMessage('Error getting bank account status', {
                    level: 'error',
                    extra: {
                        error: errorToCapture,
                        flinksAccountId
                    }
                })
                throw errorToCapture
            }
        },
        getAgreementPdf: async (applicationId: string) => {
            try {
                const clerkToken = await getToken('getAgreementPdf', applicationId)
                const { data } = await api.originationApiGeneratePdfDocuments(applicationId, parseHeaderWithClerkToken(clerkToken))
                return data
            } catch (error) {
                let errorToCapture = error
                if (isAxiosError(error) && error.response?.status === 401) {
                    try {
                        const freshClerkToken = await getToken('getAgreementPdf', applicationId, true)
                        const { data } = await api.originationApiGeneratePdfDocuments(applicationId, parseHeaderWithClerkToken(freshClerkToken))
                        return data
                    } catch (innerError) {
                        errorToCapture = innerError
                    }
                }
                captureMessage('Error getting agreement PDF', {
                    level: 'error',
                    extra: {
                        error: errorToCapture,
                        applicationId
                    }
                })
                throw errorToCapture
            }
        },
        getReviewData: async (applicationId: string) => {
            try {
                const clerkToken = await getToken('getReviewData', applicationId)
                const { data } = await api.originationApiGetReviewData(applicationId, parseHeaderWithClerkToken(clerkToken))
                return data
            } catch (error) {
                let errorToCapture = error
                if (isAxiosError(error) && error.response?.status === 401) {
                    try {
                        const freshClerkToken = await getToken('getReviewData', applicationId, true)
                        const { data } = await api.originationApiGetReviewData(applicationId, parseHeaderWithClerkToken(freshClerkToken))
                        return data
                    } catch (innerError) {
                        errorToCapture = innerError
                    }
                }
                captureMessage('Error getting review data', {
                    level: 'error',
                    extra: {
                        error: errorToCapture,
                        applicationId
                    }
                })
                throw errorToCapture
            }
        },
        generateOnfidoSdkToken: async (applicationId: string, payload: OnfidoTokenRequestApiType) => {
            try {
                const clerkToken = await getToken('generateOnfidoSdkToken', applicationId)
                const { data } = await api.originationApiGenerateOnfidoSdkToken(applicationId, payload, parseHeaderWithClerkToken(clerkToken))
                return data
            } catch (error) {
                let errorToCapture = error
                if (isAxiosError(error) && error.response?.status === 401) {
                    try {
                        const freshClerkToken = await getToken('generateOnfidoSdkToken', applicationId, true)
                        const { data } = await api.originationApiGenerateOnfidoSdkToken(applicationId, payload, parseHeaderWithClerkToken(freshClerkToken))
                        return data
                    } catch (innerError) {
                        errorToCapture = innerError
                    }
                }
                captureMessage('Error generating Onfido SDK token', {
                    level: 'error',
                    extra: {
                        error: errorToCapture,
                        applicationId
                    }
                })
                throw errorToCapture
            }
        },
        queryTmxSession: async (applicationId: string, payload: QuerySessionRequestApiType) => {
            try {
                const clerkToken = await getToken('queryTmxSession', applicationId)
                const { data } = await api.originationApiQueryTmxSession(applicationId, payload, parseHeaderWithClerkToken(clerkToken))
                return data
            } catch (error) {
                let errorToCapture = error
                if (isAxiosError(error) && error.response?.status === 401) {
                    try {
                        const freshClerkToken = await getToken('queryTmxSession', applicationId, true)
                        const { data } = await api.originationApiQueryTmxSession(applicationId, payload, parseHeaderWithClerkToken(freshClerkToken))
                        return data
                    } catch (innerError) {
                        errorToCapture = innerError
                    }
                }
                captureMessage('Error querying TMX session', {
                    level: 'error',
                    extra: {
                        error: errorToCapture,
                        applicationId
                    }
                })
                throw errorToCapture
            }
        },
        getOfferData: async (applicationId: string) => {
            let clerkToken = null
            try {
                clerkToken = await getToken('getOfferData', applicationId)
                const { data } = await api.originationApiOfferDetail(applicationId, parseHeaderWithClerkToken(clerkToken))
                return data
            } catch (error) {
                let errorToCapture = error
                if (isAxiosError(error) && error.response?.status === 401) {
                    try {
                        const freshClerkToken = await getToken('getOfferData', applicationId, true)
                        const { data } = await api.originationApiOfferDetail(applicationId, parseHeaderWithClerkToken(freshClerkToken))
                        return data
                    } catch (innerError) {
                        errorToCapture = innerError
                    }
                }
                captureMessage('Error getting offer data', {
                    level: 'error',
                    extra: {
                        error: errorToCapture,
                        applicationId,
                        hasClerkToken: Boolean(clerkToken)
                    }
                })
                throw errorToCapture
            }
        },
        getOnboardingSteps: async (applicationId: string) => {
            let clerkToken = null
            try {
                clerkToken = await getToken('getOnboardingSteps', applicationId)
                const { data } = await api.originationApiOnboardingSteps(applicationId, parseHeaderWithClerkToken(clerkToken))
                return data
            } catch (error) {
                let errorToCapture = error
                if (isAxiosError(error) && error.response?.status === 401) {
                    try {
                        const freshClerkToken = await getToken('getOnboardingSteps', applicationId, true)
                        const { data } = await api.originationApiOnboardingSteps(applicationId, parseHeaderWithClerkToken(freshClerkToken))
                        return data
                    } catch (innerError) {
                        errorToCapture = innerError
                    }
                }
                captureMessage('Error getting onboarding steps', {
                    level: 'error',
                    extra: {
                        errorName: errorToCapture instanceof Error ? errorToCapture.name : undefined,
                        errorCode: isAxiosError(errorToCapture) ? errorToCapture.code : undefined,
                        httpStatus: isAxiosError(errorToCapture) ? errorToCapture.response?.status : undefined,
                        applicationId,
                        hasClerkToken: Boolean(clerkToken)
                    }
                })
                throw errorToCapture
            }
        },
        saveLanguagePreference: async (applicationId: string, payload: LanguagePreferenceRequestApiType) => {
            try {
                const clerkToken = await getToken('saveLanguagePreference', applicationId)
                const { data } = await api.originationApiSaveLanguagePreference(applicationId, payload, parseHeaderWithClerkToken(clerkToken))
                return data
            } catch (error) {
                let errorToCapture = error
                if (isAxiosError(error) && error.response?.status === 401) {
                    try {
                        const freshClerkToken = await getToken('saveLanguagePreference', applicationId, true)
                        const { data } = await api.originationApiSaveLanguagePreference(applicationId, payload, parseHeaderWithClerkToken(freshClerkToken))
                        return data
                    } catch (innerError) {
                        errorToCapture = innerError
                    }
                }
                captureMessage('Error saving language preference', {
                    level: 'error',
                    extra: {
                        error: errorToCapture,
                        applicationId
                    }
                })
                throw errorToCapture
            }
        },
        getApplicationStatus: async (applicationId: string) => {
            try {
                const clerkToken = await getToken('getApplicationStatus', applicationId)
                const { data } = await api.originationApiGetApplicationStatus(applicationId, parseHeaderWithClerkToken(clerkToken))
                return data
            } catch (error) {
                let errorToCapture = error
                if (isAxiosError(error) && error.response?.status === 401) {
                    try {
                        const freshClerkToken = await getToken('getApplicationStatus', applicationId, true)
                        const { data } = await api.originationApiGetApplicationStatus(applicationId, parseHeaderWithClerkToken(freshClerkToken))
                        return data
                    } catch (innerError) {
                        errorToCapture = innerError
                    }
                }
                captureMessage('Error getting application status', {
                    level: 'error',
                    extra: {
                        error: errorToCapture,
                        applicationId
                    }
                })
                throw errorToCapture
            }
        },
        updatePiiData: async (applicationId: string, payload: UpdatePiiDataRequestApiType) => {
            try {
                const clerkToken = await getToken('updatePiiData', applicationId)
                const { data } = await api.originationApiUpdatePiiData(applicationId, payload, parseHeaderWithClerkToken(clerkToken))
                return data
            } catch (error) {
                let errorToCapture = error
                if (isAxiosError(error) && error.response?.status === 401) {
                    try {
                        const freshClerkToken = await getToken('updatePiiData', applicationId, true)
                        const { data } = await api.originationApiUpdatePiiData(applicationId, payload, parseHeaderWithClerkToken(freshClerkToken))
                        return data
                    } catch (innerError) {
                        errorToCapture = innerError
                    }
                }
                captureMessage('Error updating PII data', {
                    level: 'error',
                    extra: {
                        error: errorToCapture,
                        applicationId
                    }
                })
                throw errorToCapture
            }
        },

        saveEmploymentInfo: async (applicationId: string, payload: EmploymentInfoRequestType) => {
            try {
                const clerkToken = await getToken('saveEmploymentInfo', applicationId)
                const { data } = await api.originationApiSaveEmploymentInfo(applicationId, payload, parseHeaderWithClerkToken(clerkToken))
                return data
            } catch (error) {
                let errorToCapture = error
                if (isAxiosError(error) && error.response?.status === 401) {
                    try {
                        const freshClerkToken = await getToken('saveEmploymentInfo', applicationId, true)
                        const { data } = await api.originationApiSaveEmploymentInfo(applicationId, payload, parseHeaderWithClerkToken(freshClerkToken))
                        return data
                    } catch (innerError) {
                        errorToCapture = innerError
                    }
                }
                captureMessage('Error saving employment info', {
                    level: 'error',
                    extra: {
                        error: errorToCapture,
                        applicationId
                    }
                })
                throw errorToCapture
            }
        },
        generatePaymentSchedule: async (applicationId: string, payload: V3ApplicationRepaymentScheduleRequestType) => {
            try {
                const clerkToken = await getToken('generatePaymentSchedule', applicationId)
                const { data } = await api.originationApiGenerateRepaymentSchedule(applicationId, payload, parseHeaderWithClerkToken(clerkToken))
                return data
            } catch (error) {
                let errorToCapture = error
                if (isAxiosError(error) && error.response?.status === 401) {
                    try {
                        const freshClerkToken = await getToken('generatePaymentSchedule', applicationId, true)
                        const { data } = await api.originationApiGenerateRepaymentSchedule(applicationId, payload, parseHeaderWithClerkToken(freshClerkToken))
                        return data
                    } catch (innerError) {
                        errorToCapture = innerError
                    }
                }
                captureMessage('Error generating payment schedule', {
                    level: 'error',
                    extra: {
                        error: errorToCapture,
                        applicationId,
                        payload
                    }
                })
                throw errorToCapture
            }
        },
        authorizeApplication: async (applicationId: string, payload: AuthorizeApplicationRequestApiType) => {
            try {
                const clerkToken = await getToken('authorizeApplication', applicationId)
                const { data } = await api.originationApiAuthorizeApplication(applicationId, payload, parseHeaderWithClerkToken(clerkToken))
                return data
            } catch (error) {
                let errorToCapture = error
                if (isAxiosError(error) && error.response?.status === 401) {
                    try {
                        const freshClerkToken = await getToken('authorizeApplication', applicationId, true)
                        const { data } = await api.originationApiAuthorizeApplication(applicationId, payload, parseHeaderWithClerkToken(freshClerkToken))
                        return data
                    } catch (innerError) {
                        errorToCapture = innerError
                    }
                }
                if (!isPartnerNotEnabledError(errorToCapture)) {
                    captureMessage('Error authorizing application', {
                        level: 'error',
                        extra: {
                            error: errorToCapture,
                            applicationId
                        }
                    })
                }
                throw errorToCapture
            }
        }
    }
}

/**
 * Portal API hook. Uses Clerk JWT for auth; no applicationId or redirect logic.
 * Each method returns response data (unwrapped from Axios response).
 */
export function usePortalApi() {
    const { getToken } = useAuth()
    const authTokenOverride = useAuthTokenOverride()
    const api = getNinjaAPI()

    const withAuth = async () => {
        if (authTokenOverride) {
            const token = await authTokenOverride.getToken()
            return parseHeaderWithClerkToken(token)
        }
        return getToken().then(parseHeaderWithClerkToken)
    }

    return {
        portalApiGetBorrowerDetail: async () => {
            const opts = await withAuth()
            const { data } = await api.portalApiGetBorrowerDetail(opts)
            return data
        },
        portalApiGetLoanDetail: async () => {
            const opts = await withAuth()
            const { data } = await api.portalApiGetLoanDetail(opts)
            return data
        },
        portalApiGetLoanRepaymentSchedule: async (loanId: string) => {
            const opts = await withAuth()
            const { data } = await api.portalApiGetLoanRepaymentSchedule(loanId, opts)
            return data
        },
        portalApiGetLoanTransactionHistory: async (loanId: string) => {
            const opts = await withAuth()
            const { data } = await api.portalApiGetLoanTransactionHistory(loanId, opts)
            return data
        },
        portalApiGetDisbursalHistory: async (loanId: string) => {
            const opts = await withAuth()
            const { data } = await api.portalApiGetDisbursalHistory(loanId, opts)
            return data
        },
        portalApiMakePayment: async (loanId: string, amount: number, paymentType: string) => {
            const opts = await withAuth()
            const { data } = await api.portalApiMakePayment(loanId, { amount, payment_type: paymentType }, opts)
            return data
        },
        portalApiGetDocumentHistory: async (loanId: string) => {
            const opts = await withAuth()
            const { data } = await api.portalApiGetDocumentHistory(loanId, opts)
            return data
        },
        portalApiDownloadDocument: async (loanId: string, documentId: string) => {
            const opts = await withAuth()
            const { data } = await api.portalApiDownloadDocument(loanId, documentId, opts)
            return data
        },
        portalApiGetPaymentDetail: async (loanId: string) => {
            const opts = await withAuth()
            const { data } = await api.portalApiGetPaymentDetail(loanId, opts)
            return data
        },
        portalApiGetManualPaymentDetail: async (loanId: string) => {
            const opts = await withAuth()
            const { data } = await api.portalApiGetManualPaymentDetail(loanId, opts)
            return data
        },
        portalApiGetPaymentBreakdown: async (loanId: string, payload: PaymentBreakdownRequestSchema) => {
            const opts = await withAuth()
            const { data } = await api.portalApiGetPaymentBreakdown(loanId, payload, opts)
            return data
        },
        portalApiFlinksSetAccount: async (payload: FlinksSetAccountRequestSchema) => {
            const opts = await withAuth()
            const { data } = await api.portalApiFlinksSetAccount(payload, opts)
            return data
        },
        portalApiFlinksTrack: async (payload: FlinksTrackEventRequestSchema) => {
            const opts = await withAuth()
            const { data } = await api.portalApiFlinksTrack(payload, opts)
            return data
        },
        portalApiFlinksPaymentCheck: async (payload: FlinksPaymentCheckRequestSchema) => {
            const opts = await withAuth()
            const { data } = await api.portalApiFlinksPaymentCheck(payload, opts)
            return data
        },
        portalApiChangePaymentMethodLinkBankAccount: async (loanId: string, payload: ChangePaymentMethodLinkAccountRequestSchema) => {
            const opts = await withAuth()
            const { data } = await api.portalApiChangePaymentMethodLinkBankAccount(loanId, payload, opts)
            return data
        },
        portalApiChangePaymentMethodGetPadAgreement: async (loanId: string, payload: ChangePaymentMethodGetPadAgreementRequestSchema) => {
            const opts = await withAuth()
            const { data } = await api.portalApiChangePaymentMethodGetPadAgreement(loanId, payload, opts)
            return data
        },
        portalApiPaymentScheduleChangeValidateSchedule: async (loanId: string, payload: PaymentScheduleValidationRequestSchema) => {
            const opts = await withAuth()
            const { data } = await api.portalApiPaymentScheduleChangeValidateSchedule(loanId, payload, opts)
            return data
        },
        portalApiPaymentScheduleChangeProcessSelectedDate: async (loanId: string, payload: PaymentScheduleChangeProcessSelectedDateRequestSchema) => {
            const opts = await withAuth()
            const { data } = await api.portalApiPaymentScheduleChangeProcessSelectedDate(loanId, payload, opts)
            return data
        },
        portalApiPaymentScheduleChangeComplete: async (loanId: string, payload: PaymentScheduleChangeCompleteRequestSchema) => {
            const opts = await withAuth()
            const { data } = await api.portalApiPaymentScheduleChangeComplete(loanId, payload, opts)
            return data
        },
        portalApiPaymentDeferralValidateSchedule: async (loanId: string) => {
            const opts = await withAuth()
            const { data } = await api.portalApiPaymentDeferralValidateSchedule(loanId, opts)
            return data
        },
        portalApiPaymentDeferralProcessSelectedDate: async (loanId: string, payload: PaymentDeferralProcessSelectedDateRequestSchema) => {
            const opts = await withAuth()
            const { data } = await api.portalApiPaymentDeferralProcessSelectedDate(loanId, payload, opts)
            return data
        },
        portalApiPaymentDeferralComplete: async (loanId: string, payload: PaymentDeferralCompleteRequestSchema) => {
            const opts = await withAuth()
            const { data } = await api.portalApiPaymentDeferralComplete(loanId, payload, opts)
            return data
        },
        portalApiPaymentDeferralGetDeferralForm: async (loanId: string) => {
            const opts = await withAuth()
            const { data } = await api.portalApiPaymentDeferralGetDeferralForm(loanId, opts)
            return data
        },
        portalApiPaymentScheduleChangeGetPadAgreement: async (loanId: string) => {
            const opts = await withAuth()
            const { data } = await api.portalApiPaymentScheduleChangeGetPadAgreement(loanId, opts)
            return data
        },
        portalApiPaymentProtectionGetCertificateOfInsurance: async (loanId: string) => {
            const opts = await withAuth()
            const { data } = await api.portalApiPaymentProtectionGetCertificateOfInsurance(loanId, opts)
            return data
        },
        portalApiSaveLanguagePreference: async (locale: string) => {
            const opts = await withAuth()
            const { data } = await api.portalApiSaveLanguagePreference({ locale }, opts)
            return data
        },
        portalApiProfileSettingsUpdate: async (payload: ProfileSettingsUpdateRequestSchema) => {
            const opts = await withAuth()
            const { data } = await api.portalApiProfileSettingsUpdate(payload, opts)
            return data
        },
        portalApiProfileSettingsEmailChanged: async (emailUid: string) => {
            const opts = await withAuth()
            const { data } = await api.portalApiProfileSettingsEmailChanged(emailUid, opts)
            return data
        },
        portalApiUnsubscribe: async (guid: string) => {
            const opts = await withAuth()
            const { data } = await api.portalApiUnsubscribe(guid, opts)
            return data
        },
        portalApiCheckCanDelayPayment: async (loanId: string) => {
            const opts = await withAuth()
            const { data } = await api.portalApiCheckCanDelayPayment(loanId, opts)
            return data
        }
    }
}
